import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { REVIEWS, GOOGLE_REVIEWS_URL, RATING_AVG } from "../data";

const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating} de 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={16} className={i <= rating ? "text-amber-400" : "text-border"} fill={i <= rating ? "currentColor" : "none"} />
    ))}
  </div>
);

export const Testimonials = () => {
  return (
    <section id="testimonios" className="bg-muted py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-electric">Reseñas</span>
            <h2 className="font-heading font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-5">
              La confianza de nuestros clientes.
            </h2>
            <div className="mt-6 inline-flex items-center gap-3 bg-white border border-border px-4 py-2.5" data-testid="rating-summary">
              <GoogleG size={20} />
              <span className="font-heading font-bold text-ink text-lg">{RATING_AVG}</span>
              <Stars rating={5} />
              <span className="text-sm text-muted-foreground">en Google</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="inline-flex items-center justify-center gap-2 bg-white border border-border px-5 py-3 font-semibold text-ink hover:border-electric transition-colors">
              <GoogleG /> Ver reseñas <ExternalLink size={15} className="text-muted-foreground" />
            </a>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-review-button" className="inline-flex items-center justify-center gap-2 bg-ink text-white px-5 py-3 font-semibold hover:bg-electric transition-colors">
              Déjanos tu reseña
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {REVIEWS.map((t, i) => (
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
              className="bg-white border border-border p-8 lg:p-10"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center justify-between">
                <Stars rating={t.rating} />
                <GoogleG />
              </div>
              <p className="text-ink text-lg leading-relaxed mt-6 font-medium">"{t.text}"</p>
              <footer className="mt-8 flex items-center gap-4">
                <span className="w-11 h-11 grid place-items-center bg-ink text-white font-heading font-bold rounded-full">
                  {t.name.charAt(0)}
                </span>
                <div>
                  <div className="font-semibold text-ink">{t.name}</div>
                  <div className="text-sm text-muted-foreground">Opinión de Google · {t.when}</div>
                </div>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
};
